const SMART_SEARCH_SELECTOR = '[data-gl-smart-search]';
const SEARCH_ITEM_SELECTOR = '[data-gl-smart-search-item]';
const SEARCH_GROUP_SELECTOR = '[data-gl-smart-search-group]';
const SEARCH_INPUT_SELECTOR = 'input[type="search"]';
const HEADER_SEARCH_SELECTOR = '.gl-header__search-wrap';
const HEADER_SEARCH_TRIGGER_SELECTOR = '[data-gl-search-menu-trigger]';
const HEADER_SEARCH_OPEN_CLASS = 'gl-header__search-wrap--menu-open';

const directoryState = new WeakMap();

const normalize = (value) =>
  (value || '')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getSearchInput = (directory) => {
  const predictiveSearch = directory.closest('predictive-search-component');
  if (predictiveSearch) return predictiveSearch.querySelector(SEARCH_INPUT_SELECTOR);

  const headerSearch = directory.closest('.gl-header__search-wrap');
  if (headerSearch) return headerSearch.querySelector(SEARCH_INPUT_SELECTOR);

  const searchHeader = directory.closest('.search-page__header');
  if (searchHeader) return searchHeader.querySelector(SEARCH_INPUT_SELECTOR);

  const modal = directory.closest('.search-modal');
  if (modal) return modal.querySelector(SEARCH_INPUT_SELECTOR);

  return document.querySelector(SEARCH_INPUT_SELECTOR);
};

const getInitialQuery = (input) => {
  const inputValue = input?.value?.trim();
  if (inputValue) return inputValue;

  const params = new URLSearchParams(window.location.search);
  return params.get('q') || '';
};

const getItemHaystack = (item) => {
  if (!item.dataset.glSearchHaystack) {
    item.dataset.glSearchHaystack = normalize(`${item.textContent || ''} ${item.dataset.keywords || ''}`);
  }

  return item.dataset.glSearchHaystack;
};

const itemMatchesQuery = (item, query) => {
  if (!query) return true;

  const haystack = getItemHaystack(item);
  const terms = normalize(query).split(' ').filter(Boolean);

  return terms.every((term) => haystack.includes(term));
};

const updatePreviewLinks = (directory) => {
  const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
  if (!previewThemeId) return;

  directory.querySelectorAll('a[href^="/"]').forEach((link) => {
    const url = new URL(link.getAttribute('href'), window.location.origin);
    if (!url.searchParams.has('preview_theme_id')) {
      url.searchParams.set('preview_theme_id', previewThemeId);
    }
    link.href = `${url.pathname}${url.search}${url.hash}`;
  });
};

const updateDirectory = (directory, query) => {
  const rawQuery = (query || '').toString();
  const cleanQuery = normalize(rawQuery);
  const items = Array.from(directory.querySelectorAll(SEARCH_ITEM_SELECTOR));
  const groups = Array.from(directory.querySelectorAll(SEARCH_GROUP_SELECTOR));
  const empty = directory.querySelector('[data-gl-smart-search-empty]');
  const status = directory.querySelector('[data-gl-smart-search-status]');

  let matchCount = 0;

  items.forEach((item) => {
    const isMatch = itemMatchesQuery(item, cleanQuery);
    item.hidden = !isMatch;
    item.setAttribute('aria-hidden', String(!isMatch));
    if (isMatch) matchCount += 1;
  });

  groups.forEach((group) => {
    const hasVisibleItems = Boolean(group.querySelector(`${SEARCH_ITEM_SELECTOR}:not([hidden])`));
    group.hidden = !hasVisibleItems;
  });

  if (empty) empty.hidden = matchCount > 0;

  if (status) {
    status.textContent = cleanQuery
      ? `Showing ${matchCount} suggested path${matchCount === 1 ? '' : 's'} for "${rawQuery.trim()}".`
      : 'Jump straight into the main pages, portfolio paths, booking flow, and studio notes.';
  }
};

const attachInput = (directory, input) => {
  const previousState = directoryState.get(directory);

  if (previousState?.input === input) return;

  if (previousState?.input && previousState?.handler) {
    previousState.input.removeEventListener('input', previousState.handler);
    previousState.input.removeEventListener('search', previousState.handler);
    previousState.input.removeEventListener('keyup', previousState.handler);
  }

  if (!input) {
    directoryState.set(directory, { input: null, handler: null });
    return;
  }

  const handler = () => updateDirectory(directory, input.value);
  input.addEventListener('input', handler);
  input.addEventListener('search', handler);
  input.addEventListener('keyup', handler);

  directoryState.set(directory, { input, handler });
};

const setHeaderDirectoryOpen = (searchWrap, isOpen) => {
  searchWrap.classList.toggle(HEADER_SEARCH_OPEN_CLASS, isOpen);

  const trigger = searchWrap.querySelector(HEADER_SEARCH_TRIGGER_SELECTOR);
  if (trigger) trigger.setAttribute('aria-expanded', String(isOpen));
};

const closeHeaderDirectories = (exceptSearchWrap = null) => {
  document.querySelectorAll(HEADER_SEARCH_SELECTOR).forEach((searchWrap) => {
    if (searchWrap !== exceptSearchWrap) setHeaderDirectoryOpen(searchWrap, false);
  });
};

const initHeaderDirectory = (directory) => {
  const searchWrap = directory.closest(HEADER_SEARCH_SELECTOR);
  if (!searchWrap || searchWrap.dataset.glHeaderSearchMenuReady === 'true') return;

  const trigger = searchWrap.querySelector(HEADER_SEARCH_TRIGGER_SELECTOR);
  const input = searchWrap.querySelector(SEARCH_INPUT_SELECTOR);

  const openMenu = () => {
    closeHeaderDirectories(searchWrap);
    setHeaderDirectoryOpen(searchWrap, true);
  };

  trigger?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();

    const shouldOpen = !searchWrap.classList.contains(HEADER_SEARCH_OPEN_CLASS);
    closeHeaderDirectories(searchWrap);
    setHeaderDirectoryOpen(searchWrap, shouldOpen);
  });

  input?.addEventListener('focus', openMenu);
  input?.addEventListener('input', openMenu);

  directory.addEventListener('pointerdown', openMenu);
  directory.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('a')) {
      setHeaderDirectoryOpen(searchWrap, false);
    }
  });

  searchWrap.dataset.glHeaderSearchMenuReady = 'true';
};

const initDirectory = (directory) => {
  if (directory.dataset.glSmartSearchLinksReady !== 'true') {
    updatePreviewLinks(directory);
    directory.dataset.glSmartSearchLinksReady = 'true';
  }

  const input = getSearchInput(directory);
  updateDirectory(directory, getInitialQuery(input));
  attachInput(directory, input);
  initHeaderDirectory(directory);
};

const initAllDirectories = (root = document) => {
  root.querySelectorAll(SMART_SEARCH_SELECTOR).forEach(initDirectory);
};

const updateDirectoriesForInput = (input) => {
  const predictiveSearch = input.closest('predictive-search-component');
  const headerSearch = input.closest('.gl-header__search-wrap');
  const searchHeader = input.closest('.search-page__header');
  const scope = predictiveSearch || headerSearch || searchHeader || document;

  scope.querySelectorAll(SMART_SEARCH_SELECTOR).forEach((directory) => {
    updateDirectory(directory, input.value);
    attachInput(directory, input);
  });
};

const scheduleInit = (root = document) => {
  requestAnimationFrame(() => initAllDirectories(root));
};

scheduleInit();

document.addEventListener('DOMContentLoaded', () => scheduleInit());
window.addEventListener('load', () => scheduleInit());

document.addEventListener('input', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || !target.matches(SEARCH_INPUT_SELECTOR)) return;
  updateDirectoriesForInput(target);
});

document.addEventListener('keyup', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || !target.matches(SEARCH_INPUT_SELECTOR)) return;
  updateDirectoriesForInput(target);
});

document.addEventListener('click', (event) => {
  if (event.target instanceof Element && event.target.closest(HEADER_SEARCH_SELECTOR)) return;
  closeHeaderDirectories();
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  closeHeaderDirectories();
});

const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.matches(SMART_SEARCH_SELECTOR)) initDirectory(node);
      scheduleInit(node);
    });
  });
});

observer.observe(document.documentElement, {
  childList: true,
  subtree: true,
});
